// Header behaviour for sections/header.liquid. Replaces assets/header-nav.js and the inline
// .scrolled script that used to live in the section (it duplicated PART 1 below).
//
// PART 1 - DESPFY-380 .scrolled flag. The navigation row is permanently visible, and .scrolled is
// purely a "user has scrolled (or the search popup is open)" flag for the yotpo z-index rule in
// assets/header.css and the two rules in snippets/predictive-search-new.liquid. It is now also
// updated synchronously when the search popup opens/closes (via MutationObserver, before paint),
// instead of one animation frame later - that late flip was one of the causes of the popup
// flickering on open.
//
// PART 2 - hide the whole header on scroll down, bring it back on scroll up. Mobile and tablet
// only (<= 1199px). Load-bearing notes:
//
// * The element that moves is #header-group, not header-component. #header-group carries
//   `position: sticky`; moving header-component instead would leave an empty pinned box behind.
//
// * transform, not `top` - compositor-only, no sticky re-resolve per frame.
//
// * No `will-change: transform` - it would make #header-group a containing block for fixed
//   descendants permanently. See is-locked.
//
// * is-locked. The scroll-lock guard in assets/header.css switches header-component to
//   `position: fixed` (open dialogs, inline overflow locks from the Live TV app, etc.). A fixed
//   element inside a transformed ancestor anchors to the ancestor, so is-locked drops the
//   transform instantly whenever a lock / dialog / focus-inside-header is present.
//
// * The two :root offsets get zeroed while the header is hidden so the PLP toolbar, filters
//   sidebar and PDP sticky ATC move in step. Restored with removeProperty so the :root
//   declarations in header.css stay the single source of truth.
//
// * 1199px, not 1200px - header.css matches both max-width:1200 and min-width:1200 at exactly
//   1200px; arming at <= 1199 keeps this feature out of that overlap.
//
// PART 3 - space reservation (fixes the search popup "jump" on mobile and desktop).
// Whenever header-component is taken out of flow by the scroll-lock guard (position: fixed), its
// section would collapse and everything below it - the page, and on mobile the search row - would
// jump up under the header, while the header itself jumped to top: 0 over the announcement bar.
// This reserves exactly the header's measured height inside its own section and pins the fixed
// header at the same spot it occupied in flow, so the switch is invisible.
// The decision is made from the computed position, so it can never drift out of sync with the
// CSS selectors that cause it. Runs inside MutationObserver callbacks (microtasks), i.e. before
// the browser paints the frame in which the dialog / lock appeared.
//
// PART 4 - search popup alignment. Closes any gap between the bottom of the header (mobile: the
// search row) and the top of dialog.search-modal-new, by measurement. See alignSearchModal().

(() => {
  const MOBILE_QUERY = '(max-width: 1199px)';

  // Nothing hides until the user is this far down.
  const START_AFTER = 200;

  // Movement thresholds. Below these the reading is discarded WITHOUT resetting the reference
  // point, so a slow drag still accumulates to a flip; jitter never does.
  const HIDE_DELTA = 8;
  const SHOW_DELTA = 4;

  const init = () => {
    const header = document.querySelector('header-component[sticky="always"]');
    if (!header) return;

    const root = document.documentElement;
    const body = document.body;
    const group = document.getElementById('header-group');
    const mql = window.matchMedia(MOBILE_QUERY);

    let scheduled = false;
    let scrolled = null; // .scrolled flag state
    let hidden = false; // header currently translated out of view
    let locked = false; // transform suppressed
    let armed = false; // viewport is mobile or tablet
    let lastY = window.scrollY;
    let headerHeight = 0; // border-box height of header-component, kept live by ResizeObserver
    let reserved = ''; // padding-top currently written on #header-group

    const isSearchOpen = () => !!document.querySelector('.search-modal-new[open]');

    // Any of these means the header must be on screen AND must not be carrying a transform.
    const isBlocked = () =>
      root.hasAttribute('scroll-lock') ||
      (root.getAttribute('style') || '').includes('overflow') ||
      (body.getAttribute('style') || '').includes('overflow') ||
      body.classList.contains('overflow-hidden') ||
      !!document.querySelector('dialog[open], .menu-open') ||
      (!!group && group.contains(document.activeElement));

    // ---- Part 1 ----
    const setScrolled = (y) => {
      const on = y > 0 || isSearchOpen();
      if (on === scrolled) return false;
      scrolled = on;
      header.classList.toggle('scrolled', on);
      return true;
    };

    // ---- Part 2 ----
    const setHidden = (next) => {
      if (!group || next === hidden) return;
      hidden = next;
      group.classList.toggle('is-hidden', next);

      if (next) {
        root.style.setProperty('--shoplc-header-row', '0px');
        root.style.setProperty('--shoplc-nav-offset', '0px');
      } else {
        root.style.removeProperty('--shoplc-header-row');
        root.style.removeProperty('--shoplc-nav-offset');
      }
    };

    const setLocked = (next) => {
      if (!group || next === locked) return;
      locked = next;
      group.classList.toggle('is-locked', next);
    };

    // ---- Part 3 ----
    // While fixed, the header is pinned exactly where it sat in flow (below the announcement bar),
    // not at top: 0 - at top: 0 it covered the announcement bar and the search popup ended up
    // misaligned by the bar's height. Its space is reserved inside its own section (the element
    // directly around header-component), so the announcement bar above and the mobile search row
    // below do not move at all.
    const spacer = header.parentElement;
    const spacerPad = spacer ? parseFloat(getComputedStyle(spacer).paddingTop) || 0 : 0;
    const spacerBorder = spacer ? parseFloat(getComputedStyle(spacer).borderTopWidth) || 0 : 0;
    let fixedTop = '';

    const placeFixed = () => {
      if (!reserved || !spacer) return;
      // Where the header's slot is right now. If sticky has been broken by a scroll lock, the slot
      // is off-screen (negative) and the header falls back to the top of the viewport - which is
      // exactly what the scroll-lock guard exists for.
      const top = Math.max(0, Math.round(spacer.getBoundingClientRect().top + spacerBorder + spacerPad));
      const next = top + 'px';
      if (next === fixedTop) return;
      fixedTop = next;
      header.style.top = next;
    };

    const syncFlow = () => {
      if (!spacer) return;
      const fixed = getComputedStyle(header).position === 'fixed';
      const next = fixed ? spacerPad + (headerHeight || header.offsetHeight) + 'px' : '';

      if (next !== reserved) {
        reserved = next;
        spacer.style.paddingTop = next;
        if (!next) {
          fixedTop = '';
          header.style.top = '';
        }
      }

      placeFixed();
    };

    // ---- Part 4 ----
    // Search popup alignment. The popup's own `top` comes from snippets/predictive-search-new.liquid
    // and does not always match the real bottom of the header (announcement bar height, mobile
    // search row, sticky vs fixed), which showed up as a strip of page content between the search
    // bar and the results panel. This measures the actual gap and closes it.
    // Only a gap BELOW the header is corrected (0 < gap < 200px) - a popup that deliberately
    // overlaps or covers the header is left exactly as the snippet designed it.
    let alignedModal = null;

    const clearModalAlign = () => {
      if (!alignedModal) return;
      alignedModal.style.top = '';
      alignedModal.style.marginTop = '';
      alignedModal = null;
    };

    const alignSearchModal = () => {
      const modal = document.querySelector('dialog.search-modal-new[open]');
      if (!modal) {
        clearModalAlign();
        return;
      }
      if (!spacer) return;

      // Measure from the snippet's own position, not from a previous correction.
      if (alignedModal) clearModalAlign();

      const anchor = spacer.getBoundingClientRect().bottom; // bottom of logo row + mobile search row
      const gap = Math.round(modal.getBoundingClientRect().top - anchor);
      if (gap <= 0 || gap >= 200) return;

      const cs = getComputedStyle(modal);
      if (cs.position === 'static') return;

      if (cs.top !== 'auto') {
        modal.style.top = parseFloat(cs.top) - gap + 'px';
      } else {
        modal.style.marginTop = (parseFloat(cs.marginTop) || 0) - gap + 'px';
      }
      alignedModal = modal;

      // Re-check once any open animation has finished, in case it was measured mid-animation.
      modal.addEventListener('animationend', () => requestAnimationFrame(alignSearchModal), { once: true });
    };

    let resizeQueued = false;
    window.addEventListener(
      'resize',
      () => {
        if (resizeQueued || !document.querySelector('dialog.search-modal-new[open]')) return;
        resizeQueued = true;
        requestAnimationFrame(() => {
          resizeQueued = false;
          alignSearchModal();
        });
      },
      { passive: true }
    );

    // Scroll path - batched to one run per frame.
    const update = () => {
      scheduled = false;

      const y = window.scrollY;
      if (setScrolled(y)) syncFlow();
      else placeFixed(); // only does work while the header is fixed

      if (!armed || !group) {
        lastY = y;
        return;
      }

      const blocked = isBlocked();
      setLocked(blocked);

      if (blocked) {
        setHidden(false);
        lastY = y;
        return;
      }

      const delta = y - lastY;

      if (y <= START_AFTER) {
        // Covers iOS rubber-band overscroll too, where y goes negative.
        setHidden(false);
      } else if (delta > HIDE_DELTA) {
        setHidden(true);
      } else if (delta < -SHOW_DELTA) {
        setHidden(false);
      } else {
        return; // under threshold - leave lastY alone so the movement keeps accumulating
      }

      lastY = y;
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    };

    // State path - dialogs opening/closing, scroll locks. Runs synchronously (from
    // MutationObserver microtasks) so all three parts settle in the same frame as the change.
    const refresh = () => {
      setScrolled(window.scrollY);

      if (armed && group) {
        const blocked = isBlocked();
        setLocked(blocked);
        if (blocked) setHidden(false);
      }

      syncFlow();
      alignSearchModal();
    };

    const applyArmed = () => {
      armed = mql.matches;
      if (group) group.classList.toggle('shoplc-hide-scroll', armed);
      if (!armed) {
        setHidden(false);
        setLocked(false);
      }
      lastY = window.scrollY;
    };

    applyArmed();
    update();
    syncFlow();

    if ('ResizeObserver' in window) {
      new ResizeObserver((entries) => {
        const entry = entries[entries.length - 1];
        const box = entry.borderBoxSize && entry.borderBoxSize[0];
        headerHeight = Math.round(box ? box.blockSize : header.offsetHeight);
        if (reserved) {
          reserved = '';
          syncFlow();
        }
      }).observe(header);
    }

    mql.addEventListener('change', applyArmed);
    document.addEventListener('scroll', schedule, { passive: true });

    // <html>/<body> locks: inline style, the theme's scroll-lock attribute, utility classes.
    const lockObserver = new MutationObserver(refresh);
    lockObserver.observe(root, { attributes: true, attributeFilter: ['style', 'scroll-lock', 'class'] });
    lockObserver.observe(body, { attributes: true, attributeFilter: ['style', 'class'] });

    // Any dialog / details opening or closing anywhere in the page (search popup, cart drawer,
    // menu drawer). Only the `open` attribute is watched, so this stays cheap.
    new MutationObserver(refresh).observe(body, {
      attributes: true,
      attributeFilter: ['open'],
      subtree: true,
    });

    document.addEventListener('click', schedule, { passive: true, capture: true });
    document.addEventListener('focusin', schedule, { passive: true });
    document.addEventListener('focusout', schedule, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
